import { useEffect, useState } from "react";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import Dropdown from "react-bootstrap/Dropdown";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteIcon,
  HotspotImageIcon,
  HotspotTextIcon,
  LayersIcon,
  PlusIcon,
  SceneIcon,
  StarDefaultIcon,
} from "../../../assets/icons/icons";
import apiclient from "../../../apiclient";
import {
  HOTSPOT_DELETE,
  SCENE_CLONE,
  SCENE_CREATE,
  SCENE_DELETE,
  SCENE_LIST,
  SCENE_SET_DEFAULT,
} from "../../../apiclient/apiEndpoints";
import LibraryPopup from "../../../components/modals/LibraryPopup";
import useDebounce from "../../../hooks/useDebounce";
import InfiniteScroll from "react-infinite-scroll-component";
import { CLONE, DELETE, HOTSPOT, SCENE } from "../../../constant/constant";
import { resetSceneList, saveSceneList } from "../../../store/slice/projectSlice";
import BtnLoad from "../../../components/common/BtnLoad";
import IconPopup from "../../../components/modals/IconsPopup";
import { defaultScene } from "../dummy";

const { useUnoSelector } = UnoStoreHooks;
const { SearchBar, ConfirmBox } = UiComponents;

const defaultAction = {
  [DELETE]: false,
  [CLONE]: false,
  [SCENE]: false,
  [HOTSPOT]: false,
  uuid: null,
};

const Layers = ({
  animateToHotspotPosition,
  setActiveTab,
  setScene,
  scene,
  projectId,
  fetchSceneDetails,
  currentHotspot,
  setCurrentHotspot,
  createNewHotspot,
  setSelectMode,
}) => {
  const dispatch = useDispatch();
  const { sceneList, fetchSceneRef } = useSelector((state) => state.projectSlice);
  const configData = useUnoSelector((state) => state.config);
  const [activeItems, setActiveItems] = useState(null);
  const [activeSceneId, setActiveSceneId] = useState(null);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [actionModal, setActionModal] = useState({ ...defaultAction });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showHotspotIconModal, setShowHotspotIconModal] = useState(false);
  const [loadingScenes, setLoadingScenes] = useState(true);
  let searchValue = useDebounce(search, 500);

  const handleToggle = (projectId) => {
    setOpenDropdownId((prev) => (prev === projectId ? null : projectId));
  };

  const toggleSceneActive = (uuid) => {
    setActiveTab(SCENE);
    setActiveItems((pre) => (pre === uuid ? null : uuid));
    setActiveSceneId(uuid);
    if (scene?.uuid !== uuid) {
      setCurrentHotspot({});
      fetchSceneDetails(uuid);
    }
  };

  const handleSelectHotspot = (uuid) => {
    setTimeout(() => {
      let hotspotData = scene?.hotspots?.find((e) => e.uuid === uuid);
      setCurrentHotspot(hotspotData);
      setActiveTab(HOTSPOT);
      animateToHotspotPosition(hotspotData.position);
    }, 100);
  };

  useEffect(() => {
    setActiveTab(HOTSPOT);
  }, [currentHotspot.uuid]);
  useEffect(() => {
    setActiveItems(scene.uuid);
  }, [scene.uuid]);

  const getProjectSceneList = async (setDefault) => {
    try {
      let data = await apiclient.get(`${configData.config.API_URL}${SCENE_LIST}/${projectId}`);
      dispatch(saveSceneList({ data: data }));
      setLoadingScenes(setDefault);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetSceneList());
    setLoading(true);
    getProjectSceneList(true);
  }, [projectId]);

  useEffect(() => {
    getProjectSceneList(false);
  }, [fetchSceneRef]);

  const handleCreateScene = async () => {
    if (sceneLoading) return;
    try {
      setSceneLoading(true);
      let data = await apiclient.post(`${configData.config.API_URL}${SCENE_CREATE}/${projectId}`);
      setScene({
        ...data,
        inventory: defaultScene.inventory
      });
      let newScene = {
        default: false,
        hotspots: [],
        name: data.name,
        uuid: data.uuid,
      };
      dispatch(saveSceneList({ data: [...sceneList, newScene] }));
      toggleSceneActive(data.uuid);
    } catch (error) {
      console.log(error);
    } finally {
      setSceneLoading(false);
    }
  };

  const handleSceneAction = (type, uuid, mode) => {
    setActionModal({
      ...defaultAction,
      [type]: true,
      [mode]: true,
      uuid: uuid,
    });
  };

  const handleCloseAction = () => {
    setActionModal({ ...defaultAction });
  };

  const handleDeleteScene = async () => {
    if (actionModal.loading) return;
    setActionModal((pre) => ({ ...pre, loading: true }));
    try {
      await apiclient.get(`${configData.config.API_URL}${SCENE_DELETE}/${actionModal.uuid}`);
      getProjectSceneList();
    } catch (error) {
      console.log(error);
    } finally {
      setActionModal(defaultAction);
    }
  };

  const updateSceneHotspot = (hotspotUuid) => {
    if (!scene?.uuid) return;
    let _list = sceneList.map((preScene) =>
      preScene.uuid === scene.uuid
        ? { ...preScene, hotspots: preScene.hotspots?.filter((h) => h.uuid !== hotspotUuid) }
        : preScene
    );
    dispatch(saveSceneList({ data: _list }));
  };

  const updateSceneDetails = (hotspotUuid) => {
    setScene((pre) => ({
      ...pre,
      hotspots: pre.hotspots.filter((h) => h.uuid !== hotspotUuid),
    }));
  };

  const handleDeleteHotspot = async () => {
    if (actionModal.loading) return;
    setActionModal((pre) => ({ ...pre, loading: true }));
    try {
      await apiclient.get(`${configData.config.API_URL}${HOTSPOT_DELETE}/${actionModal.uuid}`);
      setCurrentHotspot({});
      updateSceneHotspot(actionModal.uuid);
      updateSceneDetails(actionModal.uuid);
    } catch (error) {
      console.log(error);
    } finally {
      setActionModal(defaultAction);
    }
  };

  const handleCloneScene = async () => {
    if (actionModal.loading) return;
    setActionModal((pre) => ({ ...pre, loading: true }));
    try {
      await apiclient.get(`${configData.config.API_URL}${SCENE_CLONE}/${actionModal.uuid}`);
      getProjectSceneList();
    } catch (error) {
      console.log(error);
    } finally {
      setActionModal(defaultAction);
    }
  };

  const handleSetSceneDefault = async (uuid) => {
    if (defaultLoading) return;
    setDefaultLoading(true);
    try {
      await apiclient.post(`${configData.config.API_URL}${SCENE_SET_DEFAULT}/${uuid}`);
      getProjectSceneList();
    } catch (error) {
      console.log(error);
    } finally {
      setDefaultLoading(false);
    }
  };

  const handleOpenNewHotspotModal = (e) => {
    setSelectMode(true);
    // e.stopPropagation();
    // setActiveTab(HOTSPOT);
    // setCurrentHotspot({
    //   ...defaultNewHotspot,
    // });
    // setShowHotspotIconModal(true);
  };

  const handleSelectHotspotInventory = (item) => {
    let inventory = {
      path: item.path,
      type: item.fileType,
      uuid: item.uuid,
    };
    createNewHotspot({ inventory });
    setTimeout(() => setShowHotspotIconModal(false), 1000);
  };

  let filterList = sceneList.filter((e) => e?.name?.toLowerCase().includes(searchValue.toLowerCase()));
  useEffect(() => {
    if (loadingScenes && sceneList.length) {
      let defaultSceneId = (sceneList.find((e) => e.default) || sceneList[0]).uuid;
      toggleSceneActive(defaultSceneId);
      setLoadingScenes(false);
    }
  }, [sceneList, loadingScenes]);
  return (
    <>
      <div className="toolbarLayers">
        {/* <div className="customSide d-flex align-items-center">
        <div className="customTabs">
          <span className="active">All Scene</span>
          <span className="addSceneBtn" onClick={() => setShowLibrary(true)}>
            <PlusIcon />
            Add Scene
          </span>
        </div>
      </div> */}
        <div className="searchNAdd">
          <div className="searchParent">
            <SearchBar placeholder="Search ..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <span className="addScene" title="Add Scene" onClick={handleCreateScene}>
            {sceneLoading ? <BtnLoad /> : <PlusIcon />}
          </span>
        </div>
        <div className="tabData">
          {loading ? (
            <div className="layersParent">
              <LayerItemSkeleton length={5} />
            </div>
          ) : filterList.length === 0 ? (
            <NoScene onClick={handleCreateScene} />
          ) : (
            <div className="layersParent" id="scrollableDivGroups">
              <InfiniteScroll
                scrollThreshold={0.5}
                dataLength={filterList.length}
                hasMore={false}
                loader={<LayerItemSkeleton length={5} />}
                scrollableTarget="scrollableDivGroups"
              >
                <>
                  {filterList.map((scene, index) => (
                    <div
                      key={index}
                      className={`layerParentItem ${activeItems === scene.uuid ? "active" : ""} ${
                        scene.default ? "default" : ""
                      }`}
                    >
                      <div className="layerItem" onClick={() => toggleSceneActive(scene.uuid)}>
                        <span className="arrowIcon">{scene.hotspots.length > 0 && <LayersIcon />}</span>
                        <span className="layerImage">
                          <SceneIcon />
                        </span>
                        <p title={scene.name}>
                          {/* {index === 0 && <StarDefaultIcon />}  */}
                          {scene.name}
                        </p>
                        {activeSceneId === scene.uuid && (
                          <span className="deleteBtn" title="Add Hotspot" onClick={(e) => handleOpenNewHotspotModal(e)}>
                            {" "}
                            <PlusIcon />{" "}
                          </span>
                        )}

                        {scene.default && (
                          <span className="ms-1 defaultIcon" title="Default Scene">
                            {" "}
                            <StarDefaultIcon />{" "}
                          </span>
                        )}
                        <div className="layerItemActions ">
                          <div className="dropdown-wrapper" onClick={(e) => e.stopPropagation()}>
                            <div className="userdrop">
                              <Dropdown show={openDropdownId === scene.uuid} onToggle={() => handleToggle(scene.uuid)}>
                                <Dropdown.Toggle id="dropdown-basic ">
                                  <div className="options">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-three-dots-vertical"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                                    </svg>
                                  </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="sidebartopbar modulelistoptions">
                                  <div className="listoptionsmodule">
                                    <span onClick={() => handleSetSceneDefault(scene.uuid)}>Set Default</span>
                                    <span onClick={() => handleSceneAction(DELETE, scene.uuid, SCENE)}>
                                      Delete Scene
                                    </span>
                                    <span onClick={() => handleSceneAction(CLONE, scene.uuid, SCENE)}>Clone Scene</span>
                                  </div>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                      </div>

                      {activeItems === scene.uuid &&
                        scene.hotspots.length > 0 &&
                        scene.hotspots.map((hotspot, hIndex) => (
                          <>
                            <div
                              key={hIndex}
                              className={`layerChild ${currentHotspot.uuid === hotspot.uuid ? "active" : ""}`}
                              onClick={() => handleSelectHotspot(hotspot.uuid)}
                            >
                              <span className="hotIcon">
                                {" "}
                                {hotspot.type === "TEXT" ? <HotspotTextIcon /> : <HotspotImageIcon />}{" "}
                              </span>
                              <p>{hotspot.name} </p>

                              <span
                                className="deleteBtn ms-1"
                                title="Delete Hotspot"
                                onClick={() => handleSceneAction(DELETE, hotspot.uuid, HOTSPOT)}
                              >
                                {" "}
                                <DeleteIcon />{" "}
                              </span>
                            </div>
                          </>
                        ))}
                    </div>
                  ))}
                </>
              </InfiniteScroll>
            </div>
          )}
        </div>
      </div>

      <LibraryPopup show={showLibrary} handleClose={() => setShowLibrary(false)} />

      <ConfirmBox
        showModal={actionModal[DELETE] || actionModal[CLONE]}
        onHide={handleCloseAction}
        alertTitle={actionModal[DELETE] ? `Delete ${actionModal[SCENE] ? "Scene" : "Hotspot"}` : "Make a clone"}
        alertMessage={
          actionModal[DELETE]
            ? `This ${
                actionModal[SCENE] ? "Scene" : "Hotspot"
              } will be deleted and can not be recovered. Are you sure you want to continue?`
            : "Do you really want to clone this scene?"
        }
        onActionClick={
          actionModal[DELETE] ? (actionModal[SCENE] ? handleDeleteScene : handleDeleteHotspot) : handleCloneScene
        }
        clickLoading={actionModal.loading}
        actionButtonItem={actionModal[DELETE] ? "Delete" : "Yes"}
      />

      <IconPopup
        hotspotId={null}
        show={showHotspotIconModal}
        handleClose={() => setShowHotspotIconModal(false)}
        handleChangeHotspot={handleSelectHotspotInventory}
      />
    </>
  );
};

export default Layers;

export const NoScene = ({ onClick }) => {
  return (
    <>
      <div className="NoLayersParent" onClick={onClick}>
        <div className="NoLayers">
          <p style={{ marginBottom: "8px" }}>No scenes yet.</p>
          <p style={{ marginBottom: "12px" }}>
            Create your first scene by clicking here or the <strong>“+”</strong> button above.
          </p>
        </div>
      </div>
    </>
  );
};

export const LayerItemSkeleton = ({ length = 5 }) => {
  return (
    <>
      {Array.from({ length: length }).map((_, index) => (
        <div className="layerParentItem" key={index}>
          <div className="layerItem">
            <p className="w-100 h-100 p-0">
              <Skeleton width="100%" height="100%" />
            </p>
          </div>
        </div>
      ))}
    </>
  );
};
